import { Box, Button, Flex, FormControl, FormLabel, Heading, Stack, Text } from "@chakra-ui/react";
import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import Select from "react-select";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import MainInput from "../../../../components/Form/input";
import MainModel from "../../../../components/Models/main-model";
import { brandPalette } from "../../../../constants/brandPalette";
import {
  adminModalBodyProps,
  adminModalContentProps,
  adminModalHeaderProps,
  adminSelectStyles,
} from "../../../../components/Models/admin-modal-theme";
import { getAllChaptersRequest } from "../../../../modules/admin-modules/chapters/Actions";
import { createLiveVideoRequest } from "../../../../modules/admin-modules/liveVideos/Actions";
import { closeAddLiveVideo } from "../../../../modules/admin-modules/models/Actions";
import { editAdminSectionRequest } from "../../../../modules/admin-modules/sections/Actions";

const AddLiveVideo = ({ isAdd, data, setSelectedData }) => {
  const openChapter = useSelector((state) => state.model.isOpen34);
  const isLoading = useSelector((state) => state.adminLiveVideos.isLoading);
  const chapters = useSelector((state) => state.chapters.chapters);
  const chaptersLoading = useSelector((state) => state.chapters.isLoading);

  const dispatch = useDispatch();

  const activeChapters = useMemo(
    () =>
      Array.isArray(chapters)
        ? chapters.filter((c) => Number(c?.active) === 1)
        : [],
    [chapters]
  );

  const chapterOptions = useMemo(
    () => activeChapters.map((c) => ({ value: c.id, label: c.name })),
    [activeChapters]
  );

  useEffect(() => {
    if (!openChapter) return;
    if (!Array.isArray(chapters) || chapters.length === 0) {
      dispatch(getAllChaptersRequest());
    }
  }, [openChapter, chapters, dispatch]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: !isAdd
      ? {
        ...data,
        title: data?.title,
        zoom_link: data?.zoom_link,
        chapter_id: data?.chapter_id ?? data?.chapter?.id,
      }
      : {},
    shouldUnregister: true,
  });

  const chapterId = watch("chapter_id");
  const selectedChapterOption = useMemo(
    () =>
      chapterOptions.find((o) => String(o.value) === String(chapterId)) ?? null,
    [chapterOptions, chapterId]
  );

  const selectStyles = adminSelectStyles();

  const header = (
    <Box>
      <Heading
        fontSize="32px"
        fontWeight="800"
        color={brandPalette.primary}
        fontFamily="'Amiri','Tajawal',sans-serif"
      >
        {isAdd ? "Add Live Session" : "Edit Live Session"}
      </Heading>
      <Text
        fontSize="xs"
        mt={1}
        color="var(--text-muted)"
        textTransform="uppercase"
        letterSpacing="0.14em"
        fontWeight="700"
      >
        Title and meeting link for students
      </Text>
    </Box>
  );
  const onSubmit = async (values) => {
    const action = () => {
      dispatch(closeAddLiveVideo());
      setSelectedData({});
      reset();
    };
    let formData = new FormData();
    formData.append("title", values?.title);
    formData.append("zoom_link", values?.zoom_link);
    if (isAdd) {
      if (values?.chapter_id == null || values?.chapter_id === "") {
        toast.error("Please select a lesson (active chapter)");
        return;
      }
      dispatch(
        createLiveVideoRequest(
          {
            ...values,
            chapter_id: values.chapter_id,
          },
          action
        )
      );
    } else {
      dispatch(
        editAdminSectionRequest({
          id: data?.id,
          formData: formData,
          action: action,
        })
      );
    }
  };
  const closeFunc = () => {
    dispatch(closeAddLiveVideo());
    setSelectedData({});
    reset();
  };
  return (
    <MainModel
      isLoading={isLoading}
      header={header}
      closeFunc={closeFunc}
      openVar={openChapter}
      buttonTitle={null}
      buttonFunc={handleSubmit(onSubmit)}
      size="2xl"
      contentProps={adminModalContentProps}
      headerProps={adminModalHeaderProps}
      bodyProps={adminModalBodyProps}
      hideDefaultFooter
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={6} dir="ltr" w="100%">

          <MainInput
            label="Session title"
            defaultValues={data?.title ?? data?.name}
            error={errors?.title?.message}
            placeholder="Live session name"
            register={register}
            name="title"
          />
          <MainInput
            label="Meeting link"
            defaultValues={data?.zoom_link}
            error={errors?.zoom_link?.message}
            placeholder="https://..."
            register={register}
            name="zoom_link"
          />
          <FormControl>
            <FormLabel
              fontWeight="800"
              color="var(--text-primary)"
              fontSize="sm"
              mb={2}
            >
              Lesson
            </FormLabel>
            <Select
              styles={selectStyles}
              options={chapterOptions}
              value={selectedChapterOption}
              onChange={(opt) =>
                setValue("chapter_id", opt?.value ?? "", {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              placeholder="Select lesson…"
              isClearable
              isDisabled={!isAdd}
              isLoading={Boolean(chaptersLoading)}
              noOptionsMessage={() =>
                chaptersLoading
                  ? "Loading lessons…"
                  : "No active lessons — activate a chapter in Lessons first"
              }
            />
          </FormControl>
          <Flex justify="flex-end" gap={6} pt={4}>
            <Button
              variant="ghost"
              color="var(--text-primary)"
              fontWeight="800"
              _hover={{ textDecoration: "underline", bg: "rgba(91, 13, 22, 0.08)" }}
              onClick={closeFunc}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              bg={brandPalette.primary}
              color="white"
              borderRadius="lg"
              px={8}
              py={6}
              fontWeight="800"
              isLoading={isLoading}
              _hover={{ bg: brandPalette.accentDark }}
              boxShadow="0 10px 24px rgba(91, 13, 22, 0.18)"
            >
              Save Changes
            </Button>
          </Flex>
        </Stack>
      </form>
    </MainModel>
  );
};

export default AddLiveVideo;
